import {
  FILENAME_DIAGNOSTICS,
  FILENAME_MODEL,
  FILENAME_SCREENSHOT,
  PART_DIAGNOSTICS,
  PART_MODEL,
  PART_MODEL_STATE,
  PART_SCREENSHOT,
  PART_SCREENSHOT_STATE,
  type ModelState,
  type ScreenshotState,
} from '@shared/support-bundles/contract'

export const BUNDLE_ENDPOINT = '/api/support-bundles'

export interface SubmitInput {
  message: string
  platform: string
  diagnostics: Uint8Array
  screenshot?: Uint8Array | undefined
  model?: Uint8Array | undefined
  screenshotState: ScreenshotState
  modelState: ModelState
  /** App JWT. The API is bearer-authenticated and sets no cookies, so without
   *  this every submission is rejected before the route runs. */
  token: string | null
}

export type SubmitResult =
  | { outcome: 'accepted'; reference: string }
  | { outcome: 'too_large' }
  | { outcome: 'rate_limited'; retryAfterSeconds?: number }
  | { outcome: 'unavailable' }
  | { outcome: 'rejected' }
  /**
   * The request never got a response — the browser could not reach us at all.
   *
   * Separate from `failed` because only this one is about the USER's network.
   * A live submission told someone to "check your connection" when their
   * connection had just carried a request to our server and back; the broken
   * link was between our task and the receiver, inside a VPC they cannot see.
   * Same conflation the backend had between `unauthorized` and `failed`, one
   * layer out, and the same cost: the sentence named the wrong party.
   */
  | { outcome: 'offline' }
  | { outcome: 'failed'; detail?: string }

type FetchImpl = (url: string, init: RequestInit) => Promise<Response>

function buildForm(input: SubmitInput, includeScreenshot: boolean): FormData {
  const form = new FormData()
  form.append('message', input.message)
  form.append('platform', input.platform)
  // Shedding the screenshot for size is its own state. Sending `included` with
  // no part would be the one combination the scheme cannot explain, and
  // `failed` would send triage after a capture bug that did not happen.
  const screenshotState: ScreenshotState =
    !includeScreenshot && input.screenshotState === 'included' ? 'too_large' : input.screenshotState
  form.append(PART_SCREENSHOT_STATE, screenshotState)
  form.append(PART_MODEL_STATE, input.modelState)

  form.append(
    PART_DIAGNOSTICS,
    new Blob([input.diagnostics as BlobPart], { type: 'application/gzip' }),
    FILENAME_DIAGNOSTICS,
  )
  // Absent stays absent. Paired with the opt-out flags this is what lets the
  // receiver tell "the user opted out" from "capture failed" — an empty part
  // would collapse the two into one indistinguishable state.
  if (includeScreenshot && input.screenshot) {
    form.append(
      PART_SCREENSHOT,
      new Blob([input.screenshot as BlobPart], { type: 'image/png' }),
      FILENAME_SCREENSHOT,
    )
  }
  if (input.model) {
    form.append(
      PART_MODEL,
      new Blob([input.model as BlobPart], { type: 'application/gzip' }),
      FILENAME_MODEL,
    )
  }
  return form
}

async function post(
  input: SubmitInput,
  includeScreenshot: boolean,
  fetchImpl: FetchImpl,
): Promise<Response> {
  return fetchImpl(BUNDLE_ENDPOINT, {
    method: 'POST',
    // Content-Type is deliberately unset: the browser has to add the multipart
    // boundary itself, and naming the type here would omit it.
    ...(input.token !== null && { headers: { authorization: `Bearer ${input.token}` } }),
    body: buildForm(input, includeScreenshot),
  })
}

async function interpret(response: Response): Promise<SubmitResult> {
  if (response.status === 202) {
    let reference: unknown
    try {
      const body = (await response.json()) as Record<string, unknown>
      // The backend mounts apiResponseWrapper() across /api, so the route's
      // { reference } arrives as { success, data: { reference } }. Reading only
      // the bare key would report every accepted bundle as a failure and prompt
      // the user to resend one the receiver already has.
      const data = body['data']
      reference =
        data !== null && typeof data === 'object'
          ? (data as Record<string, unknown>)['reference']
          : body['reference']
    } catch {
      reference = undefined
    }
    // Never invent a reference. A success toast quoting a number support cannot
    // find is worse than an honest failure.
    return typeof reference === 'string' && reference !== ''
      ? { outcome: 'accepted', reference }
      : { outcome: 'failed', detail: 'no reference returned' }
  }

  if (response.status === 429) {
    const header = response.headers.get('retry-after')
    const seconds = header === null ? Number.NaN : Number.parseInt(header, 10)
    return Number.isFinite(seconds)
      ? { outcome: 'rate_limited', retryAfterSeconds: seconds }
      : { outcome: 'rate_limited' }
  }

  if (response.status === 503) return { outcome: 'unavailable' }
  // A schema failure will not fix itself on a retry.
  if (response.status === 400) return { outcome: 'rejected' }

  return { outcome: 'failed', detail: `server returned ${response.status}` }
}

/**
 * Sends an assembled bundle, dropping the screenshot if the server says the
 * bundle is too large.
 *
 * The screenshot is the only thing shed, and only on a 413: the logs are the
 * diagnostic, the screenshot is the garnish. The retry reuses the bytes already
 * collected rather than recapturing, so the second attempt describes the same
 * moment as the first.
 */
export async function submitBundle(
  input: SubmitInput,
  fetchImpl: FetchImpl = fetch,
): Promise<SubmitResult> {
  let response: Response
  try {
    response = await post(input, true, fetchImpl)
  } catch {
    // A rejected fetch means no response existed. The detail is a browser
    // string like "Failed to fetch" that names no cause and helps nobody, and
    // the outcome already carries everything the caller acts on.
    return { outcome: 'offline' }
  }

  if (response.status === 413 && input.screenshot) {
    try {
      response = await post(input, false, fetchImpl)
    } catch {
      return { outcome: 'offline' }
    }
    if (response.status === 413) return { outcome: 'too_large' }
  } else if (response.status === 413) {
    return { outcome: 'too_large' }
  }

  return interpret(response)
}
